import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  LinearProgress,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import PageTitle from '../../components/PageTitle';
import { setPageTitle } from '../../utils/pageTitle';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

/**
 * Units of Measurement management page component
 * @returns {JSX.Element} Units page
 */
const UnitsPage = () => {
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development
  const unitNameInputRef = useRef(null);
  
  // Set page title on component mount
  useEffect(() => {
    setPageTitle("Units");
  }, []);
  
  // State management
  const [allUnits, setAllUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    abbreviation: '',
    description: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Load all units (API returns full list for company)
  const loadUnits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await unitService.getAllUnits({
        page: 0,
        size: 10000,
        sort: 'name',
        direction: 'ASC'
      });
      
      if (response && response.content) {
        setAllUnits(response.content);
      } else {
        setAllUnits(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.error('Error loading units:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load units of measurement',
        severity: 'error'
      });
      setAllUnits([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredUnits = useMemo(() => {
    if (!normalizedSearch) return allUnits;
    return allUnits.filter((u) =>
      (u.name || '').toLowerCase().includes(normalizedSearch) ||
      (u.abbreviation || '').toLowerCase().includes(normalizedSearch) ||
      (u.description || '').toLowerCase().includes(normalizedSearch)
    );
  }, [allUnits, normalizedSearch]);

  const displayedUnits = useMemo(
    () => filteredUnits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUnits, page, rowsPerPage]
  );

  const filteredCount = filteredUnits.length;

  const handleExportUnitsExcel = useCallback(() => {
    const rows = filteredUnits.map((u) => ({
      ID: u.id,
      Name: u.name || '',
      Abbreviation: u.abbreviation || '',
      Description: u.description || '',
    }));
    const ok = downloadXlsxFromRows(
      `units_of_measurement_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Units',
      rows
    );
    if (!ok) {
      setSnackbar({ open: true, message: 'Nothing to export', severity: 'info' });
    }
  }, [filteredUnits]);
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Dialog handlers
  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setSelectedUnit(unit);
      setFormValues({
        name: unit.name,
        abbreviation: unit.abbreviation,
        description: unit.description || ''
      });
    } else {
      setSelectedUnit(null);
      setFormValues({
        name: '',
        abbreviation: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUnit(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  
  // Form submission
  const handleSubmit = async () => {
    try {
      const unitData = {
        name: formValues.name,
        abbreviation: formValues.abbreviation,
        description: formValues.description,
        companyId
      };
      
      if (selectedUnit) {
        // Update existing unit
        await unitService.updateUnit(selectedUnit.id, unitData);
        setSnackbar({
          open: true,
          message: 'Unit of measurement updated successfully',
          severity: 'success'
        });
      } else {
        // Create new unit
        await unitService.createUnit(unitData);
        setSnackbar({
          open: true,
          message: 'Unit of measurement created successfully',
          severity: 'success'
        });
      }
      
      // Close the dialog and refresh data
      handleCloseDialog();
      loadUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${selectedUnit ? 'update' : 'create'} unit: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Delete handlers
  const handleOpenDeleteDialog = (unit) => {
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUnitToDelete(null);
  };
  
  const handleDeleteUnit = async () => {
    try {
      await unitService.deleteUnit(unitToDelete.id);
      setSnackbar({
        open: true,
        message: 'Unit of measurement deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete unit: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  return (
    <Layout>
      <PageTitle title="Units" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Units of Measurement
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search name, abbreviation, description…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 260 }, flex: { sm: 1 } }}
            />
            <Button variant="outlined" color="primary" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportUnitsExcel}>
              Export Excel
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add Unit
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto' }}>
          <Paper elevation={0} variant="outlined" sx={{
            p: 0,
            borderRadius: 1,
            overflow: 'hidden',
          }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell width="28%">Name</TableCell>
                    <TableCell width="14%">Abbreviation</TableCell>
                    <TableCell width="46%">Description</TableCell>
                    <TableCell align="center" width="12%">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && allUnits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : displayedUnits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        No units of measurement found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedUnits.map((unit) => (
                      <TableRow key={unit.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={unit.name}>
                            {unit.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {unit.abbreviation}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={unit.description || ''}>
                            {unit.description?.trim() ? unit.description : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" color="info" onClick={() => handleOpenDialog(unit)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(unit)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          </Paper>
        </Box>
      </Box>
      
      {/* Add/Edit Unit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                unitNameInputRef.current?.focus({ preventScroll: true });
              });
            });
          },
        }}
      >
        <DialogTitle>
          {selectedUnit ? 'Edit Unit of Measurement' : 'Add New Unit of Measurement'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              inputRef={unitNameInputRef}
              fullWidth
              label="Unit Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="e.g., Kilogram, Liter, Piece"
              autoComplete="off"
            />
            
            <TextField
              fullWidth
              label="Abbreviation"
              name="abbreviation"
              value={formValues.abbreviation}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="e.g., kg, L, pc"
              autoComplete="off"
            />
            
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formValues.description}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              helperText="Optional description of the unit"
              autoComplete="off"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formValues.name || !formValues.abbreviation}
          >
            {selectedUnit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the unit <strong>{unitToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
            Note: Units that are in use by products cannot be deleted. You must reassign products to a different unit first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            onClick={handleDeleteUnit} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default UnitsPage; 